import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LOCALE_NAMES } from '@zt/i18n';
import { Badge, Button, Dialog, Field, Input, Select, Spinner, Textarea } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { LegalDoc, LegalVersion } from '../lib/types';

const LOCALES = ['tr', 'en', 'de', 'ar'] as const;
type Locale = (typeof LOCALES)[number];
const localeName = (l: string) => LOCALE_NAMES[l as Locale] ?? l;

interface LanguageStatus {
  marketLocales: Locale[];
  gaps: Array<{ locale: Locale; missing: string[] }>;
}

/**
 * Yasal metinler: KVKK aydınlatma, üyelik sözleşmesi, ön bilgilendirme formu, mesafeli satış sözleşmesi,
 * iade/cayma, gizlilik ve çerez politikası. Taslak → yayın; yayımlanan sürüm değiştirilemez.
 * Ön bilgilendirme ve mesafeli satış metni siparişte sipariş bilgileriyle doldurulur.
 * Dil sürümleri (F7-26): her dilin ayrı taslağı, yayımı ve hukuki inceleme beyanı; Türkçe esastır.
 */
export function LegalTextsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const q = useQuery({
    queryKey: ['legal-documents'],
    queryFn: () => api.get<LegalDoc[]>('/v1/admin/legal-documents'),
  });
  const langs = useQuery({
    queryKey: ['legal-documents', 'languages'],
    queryFn: () => api.get<LanguageStatus>('/v1/admin/legal-documents/languages'),
  });
  const [open, setOpen] = useState<{ kind: string; locale: Locale } | null>(null);
  const openDoc = open && q.data?.find((d) => d.kind === open.kind);
  // Gösterilen diller: Türkçe + pazar dilleri + yayımı/taslağı olan diller.
  const shown = (d: LegalDoc) =>
    LOCALES.filter(
      (l) =>
        l === 'tr' || langs.data?.marketLocales.includes(l) || d.locales[l].published || d.locales[l].draft,
    );
  return (
    <PageFrame helpKey="page.legal">
      <p className="rounded-sm border border-amber/30 bg-amber-tint px-3 py-2 text-sm text-ink">
        {t('admin.legal.lawyerNote')}
      </p>
      {langs.data && langs.data.gaps.length > 0 && (
        <div
          className="rounded-sm border border-amber/30 bg-amber-tint px-3 py-2 text-sm text-ink"
          role="note"
        >
          <p className="flex items-center gap-1.5 font-medium">
            {t('admin.legal.languageGaps')} <HelpTip k="legal.languages" />
          </p>
          <ul className="mt-1 list-disc ps-5">
            {langs.data.gaps.map((g) => (
              <li key={g.locale}>
                {localeName(g.locale)}: {g.missing.map((k) => t(`admin.legal.kinds.${k}`)).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {q.data.map((d) => (
            <li key={d.kind} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {t(`admin.legal.kinds.${d.kind}`)}
                  {d.requiredForGoLive && (
                    <span className="ms-2 text-sm font-normal text-ink-3">{t('admin.legal.required')}</span>
                  )}
                </p>
                <p className="text-sm text-ink-2">
                  {d.published
                    ? t('admin.legal.publishedVersion', {
                        version: d.published.version,
                        date: d.published.publishedAt ? formatDate(d.published.publishedAt) : '—',
                      })
                    : t('admin.legal.notPublished')}
                  {d.templated && ` · ${t('admin.legal.templated')}`}
                </p>
                <ul className="mt-1 flex flex-wrap gap-1.5" aria-label={t('admin.legal.languages')}>
                  {shown(d).map((l) => {
                    const v = d.locales[l];
                    const label = v.published
                      ? t('admin.legal.langPublished', { version: v.published.version })
                      : v.draft
                        ? t('admin.legal.hasDraft')
                        : t('admin.legal.notPublished');
                    return (
                      <li key={l}>
                        <button
                          type="button"
                          className="rounded-sm border border-line bg-surface-2 px-2 py-0.5 text-[0.8125rem] hover:border-pine"
                          onClick={() => setOpen({ kind: d.kind, locale: l })}
                        >
                          <span className="font-medium">{localeName(l)}</span>:{' '}
                          <span className={v.published ? 'text-moss' : 'text-amber'}>{label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              {d.published && (
                <Badge tone={d.published.legalReviewed ? 'moss' : 'amber'}>
                  {d.published.legalReviewed ? t('admin.legal.reviewed') : t('admin.legal.notReviewed')}
                </Badge>
              )}
              {d.draft && <Badge tone="slate">{t('admin.legal.hasDraft')}</Badge>}
              <Button size="sm" onClick={() => setOpen({ kind: d.kind, locale: 'tr' })}>
                {d.published || d.draft ? t('common.edit') : t('admin.legal.write')}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {openDoc && open && (
        <LegalEditor
          key={`${open.kind}:${open.locale}`}
          doc={openDoc}
          locale={open.locale}
          marketLocales={langs.data?.marketLocales ?? []}
          onLocale={(locale) => setOpen({ kind: open.kind, locale })}
          onClose={() => setOpen(null)}
        />
      )}
    </PageFrame>
  );
}

function LegalEditor({
  doc,
  locale,
  marketLocales,
  onLocale,
  onClose,
}: {
  doc: LegalDoc;
  locale: Locale;
  marketLocales: Locale[];
  onLocale: (l: Locale) => void;
  onClose: () => void;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const write = can('legal.document.write');
  const publishable = can('legal.document.publish');
  const lv = doc.locales[locale];
  const start = lv.draft ?? lv.published;
  const [title, setTitle] = useState(
    start?.title ?? (locale === 'tr' ? (doc.document?.title ?? t(`admin.legal.kinds.${doc.kind}`)) : ''),
  );
  const [body, setBody] = useState(start?.body ?? '');
  const [reviewed, setReviewed] = useState(false);
  const [view, setView] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement | null>(null);
  const inv = [['legal-documents'], ['readiness'], ['setup']];
  // Son kaydedilen hâl: yayın yalnızca kaydedilmiş taslakla yapılır.
  const [baseline, setBaseline] = useState({ title, body });
  const saved = useApiMutation(
    (v: { title: string; body: string }) =>
      api.put<LegalVersion>(`/v1/admin/legal-documents/${doc.kind}/draft`, { ...v, locale }),
    {
      invalidate: inv,
      success: t('admin.legal.draftSaved'),
      silentFieldErrors: true,
      onSuccess: (_r, v) => setBaseline(v),
    },
  );
  const draftVersion = saved.data?.version ?? lv.draft?.version;
  const dirty = body !== baseline.body || title !== baseline.title;
  const publish = useApiMutation(
    () =>
      api.post(`/v1/admin/legal-documents/${doc.kind}/publish`, {
        legalReviewed: reviewed,
        version: draftVersion,
        locale,
      }),
    { invalidate: inv, success: t('admin.legal.published'), onSuccess: onClose, silentFieldErrors: true },
  );
  const discard = useApiMutation(
    () => api.del(`/v1/admin/legal-documents/${doc.kind}/draft?locale=${locale}`),
    {
      invalidate: inv,
      success: t('common.deleted'),
      onSuccess: onClose,
    },
  );
  const insert = (v: string) => {
    const el = area.current;
    const token = `{{${v}}}`;
    if (!el) return setBody(body + token);
    const a = el.selectionStart ?? body.length;
    const b = el.selectionEnd ?? body.length;
    setBody(body.slice(0, a) + token + body.slice(b));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + token.length, a + token.length);
    });
  };
  const bodyErr = fieldError(saved.error, 'body');
  const hasDraft = Boolean(lv.draft || saved.data);
  const trText = doc.locales.tr.draft ?? doc.locales.tr.published;
  const switchLocale = (l: Locale) => {
    if (l === locale) return;
    if (dirty && !window.confirm(t('admin.legal.switchLanguageConfirm'))) return;
    onLocale(l);
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={`${t(`admin.legal.kinds.${doc.kind}`)} — ${localeName(locale)}`}
      description={doc.templated ? t('admin.legal.templatedLead') : t('admin.legal.plainLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          {hasDraft && write && (
            <ConfirmButton
              message={t('admin.legal.discardConfirm')}
              onConfirm={() => discard.mutateAsync(undefined)}
            >
              {t('admin.legal.discard')}
            </ConfirmButton>
          )}
          <span className="flex-1" />
          {write && (
            <Button
              loading={saved.isPending}
              disabled={!body.trim()}
              onClick={() => saved.mutate({ title, body })}
            >
              {t('admin.legal.saveDraft')}
            </Button>
          )}
          {publishable && (
            <span className="flex items-center gap-1">
              <Button
                variant="primary"
                loading={publish.isPending}
                disabled={!hasDraft || dirty}
                onClick={() => publish.mutate(undefined)}
              >
                {t('admin.legal.publish')}
              </Button>
              <HelpTip k="legal.publish" />
            </span>
          )}
        </>
      }
    >
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <Field label={t('admin.legal.language')} help={<HelpTip k="legal.language" />} className="w-56">
          {(p) => (
            <Select {...p} value={locale} onChange={(e) => switchLocale(e.target.value as Locale)}>
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {localeName(l)}
                  {l === 'tr'
                    ? ` (${t('admin.legal.primaryLanguage')})`
                    : marketLocales.includes(l)
                      ? ` (${t('admin.legal.marketLanguage')})`
                      : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {locale !== 'tr' && write && trText && !start && (
          <span className="flex items-center gap-1">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setBody(trText.body);
                if (!title) setTitle(trText.title ?? doc.document?.title ?? '');
              }}
            >
              {t('admin.legal.copyTurkish')}
            </Button>
            <HelpTip k="legal.copy_tr" />
          </span>
        )}
      </div>
      {locale !== 'tr' && (
        <p className="mb-5 rounded-sm border border-line bg-surface-2 px-3 py-2 text-sm text-ink-2">
          {t('admin.legal.translationNote', { language: localeName(locale) })}
        </p>
      )}
      <fieldset disabled={!write} className="space-y-5">
        <Field label={t('admin.legal.title')} required error={fieldError(saved.error, 'title')}>
          {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium">{t('admin.legal.variables')}</p>
            <HelpTip k="legal.variables" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {doc.variables.map((v) => (
              <button
                key={v}
                type="button"
                className="rounded-sm border border-line bg-surface-2 px-2 py-0.5 font-mono text-[0.8125rem] hover:border-pine"
                onClick={() => insert(v)}
                title={t('admin.legal.insertVariable')}
              >
                {`{{${v}}}`}
              </button>
            ))}
          </div>
        </div>
        <Field label={t('admin.legal.body')} required error={bodyErr} description={t('admin.legal.bodyHint')}>
          {(p) => (
            <Textarea
              {...p}
              ref={area}
              className="min-h-[22rem] font-mono text-[0.875rem] leading-relaxed"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          )}
        </Field>
        {saved.error && !saved.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {saved.error.title}
          </p>
        )}
      </fieldset>
      {publishable && hasDraft && (
        <div className="mt-5 space-y-2 rounded-sm border border-line bg-surface-2 px-3 py-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 accent-[var(--zt-pine)]"
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
            />
            <span>
              {t('admin.legal.reviewedAck')} <HelpTip k="legal.reviewed" />
            </span>
          </label>
          {dirty && <p className="text-sm text-ink-3">{t('admin.legal.saveBeforePublish')}</p>}
          {publish.error && (
            <p role="alert" className="text-sm text-rose">
              {publish.error.errors.length
                ? publish.error.errors.map((e) => e.message).join(' · ')
                : publish.error.title}
            </p>
          )}
        </div>
      )}
      {lv.history.length > 0 && (
        <div className="mt-6">
          <p className="mb-2 text-sm font-medium">{t('admin.legal.history')}</p>
          <ul className="divide-y divide-line rounded-md border border-line text-sm">
            {lv.history.map((h) => (
              <li key={h.id} className="flex items-center gap-3 px-3 py-2">
                <span className="flex-1">
                  {t('admin.legal.versionLabel', { version: h.version })} ·{' '}
                  {h.publishedAt ? formatDate(h.publishedAt) : '—'}
                </span>
                <Badge tone={h.status === 'published' ? 'moss' : 'neutral'}>
                  {t(`admin.legal.statuses.${h.status}`)}
                </Badge>
                <Button size="sm" variant="ghost" onClick={() => setView(h.id)}>
                  {t('admin.legal.view')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {view && <VersionDialog id={view} onClose={() => setView(null)} />}
    </Dialog>
  );
}

function VersionDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useT();
  const q = useQuery({
    queryKey: ['legal-version', id],
    queryFn: () => api.get<LegalVersion>(`/v1/admin/legal-versions/${id}`),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={q.data ? t('admin.legal.versionLabel', { version: q.data.version }) : t('common.loading')}
      closeLabel={t('common.close')}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words font-sans text-sm">
          {q.data.body}
        </pre>
      )}
    </Dialog>
  );
}
